/**
 * IntakePage -- workspace creation screen with file upload and type selector.
 *
 * Three workspace modes:
 *   - Admission: multi-file, full 3-agent workup (HPI, PE, A&P)
 *   - Flow: text file(s), IPASS signout with Casey only
 *   - Chat: no files, direct conversation with Casey
 *
 * Ported from master JSX (IntakePage.jsx) to TypeScript with Shadcn UI.
 */

import { FC, useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { Upload, FileText, MessageSquare, X, Stethoscope, ClipboardList, Mic } from 'lucide-react'
import { cn } from '../lib/utils'
import { Button } from './ui/button'
import { Card } from './ui/card'
import { Input } from './ui/input'
import { useEncounter } from '../contexts/EncounterContext'
import { validateFile } from '../utils/fileValidation'
import type { HydratedFile, WorkspaceType } from '../services/workspaceService'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

interface IntakePageProps {
  onWorkspaceCreated: (workspaceId: string, type: WorkspaceType, files: File[]) => void
  isCreating?: boolean
  initialType?: WorkspaceType
  existingFiles?: HydratedFile[]
  existingDisplayName?: string
}

interface WorkspaceTypeOption {
  type: WorkspaceType
  label: string
  description: string
  icon: FC<{ className?: string }>
  accent: string
  accentBg: string
  accentBorder: string
  accentShadow: string
  acceptTypes: string
  requiresFiles: boolean
}

const WORKSPACE_TYPES: WorkspaceTypeOption[] = [
  {
    type: 'admission',
    label: 'Admission Panel',
    description: 'Full workup -- HPI, PE, A&P canvases with multi-agent processing.',
    icon: Stethoscope,
    accent: 'text-clinical-cyan',
    accentBg: 'bg-clinical-cyan/10',
    accentBorder: 'border-clinical-cyan/30',
    accentShadow: 'shadow-clinical-cyan/10',
    acceptTypes: 'text/*,.txt,.csv,.json,.xml,.hl7,.pdf,image/*',
    requiresFiles: true,
  },
  {
    type: 'flow',
    label: 'Flow Panel',
    description: 'Quick IPASS signout from text files. Casey processes step-by-step.',
    icon: ClipboardList,
    accent: 'text-purple-400',
    accentBg: 'bg-purple-500/10',
    accentBorder: 'border-purple-500/30',
    accentShadow: 'shadow-purple-500/10',
    acceptTypes: 'text/*,.txt',
    requiresFiles: true,
  },
  {
    type: 'chat',
    label: 'Chat',
    description: 'Direct conversation with Casey. No chart data required.',
    icon: MessageSquare,
    accent: 'text-clinical-sage',
    accentBg: 'bg-clinical-sage/10',
    accentBorder: 'border-clinical-sage/30',
    accentShadow: 'shadow-clinical-sage/10',
    acceptTypes: 'text/*,.txt,.csv,.json,.xml,.hl7,.pdf,image/*',
    requiresFiles: false,
  },
]

// ────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────

export const IntakePage: FC<IntakePageProps> = ({
  onWorkspaceCreated,
  isCreating = false,
  initialType,
  existingFiles,
  existingDisplayName,
}) => {
  const { status: encounterStatus, startEncounter } = useEncounter()
  const [selectedType, setSelectedType] = useState<WorkspaceType>(initialType ?? 'admission')
  const [files, setFiles] = useState<File[]>([])
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [patientLabel, setPatientLabel] = useState(existingDisplayName ?? '')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const isOverlayHandoff = (existingFiles?.length ?? 0) > 0

  // Sync with external type selection (e.g., sidebar dropdown)
  useEffect(() => {
    if (initialType) {
      setSelectedType(initialType)
      setFiles([])
    }
  }, [initialType])

  const selectedOption = WORKSPACE_TYPES.find(t => t.type === selectedType)!

  const addFiles = useCallback((newFiles: File[]) => {
    const valid: File[] = []
    const rejected: string[] = []
    for (const f of newFiles) {
      const result = validateFile(f)
      if (result.valid) {
        valid.push(f)
      } else {
        rejected.push(`${f.name}: ${result.error}`)
      }
    }
    if (rejected.length > 0) {
      setRejectedFiles(rejected)
      setTimeout(() => setRejectedFiles([]), 5000)
    }
    if (valid.length === 0) return
    setFiles(prev => {
      const existing = new Set(prev.map(f => `${f.name}:${f.size}`))
      const deduped = valid.filter(f => !existing.has(`${f.name}:${f.size}`))
      return [...prev, ...deduped]
    })
  }, [])

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const dropped = Array.from(e.dataTransfer.files)
    if (dropped.length > 0) addFiles(dropped)
  }, [addFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])
    if (selected.length > 0) addFiles(selected)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [addFiles])

  const handleTypeChange = useCallback((type: WorkspaceType) => {
    setSelectedType(type)
    setFiles([]) // Clear files when switching type
  }, [])

  // Memoize object URLs for image previews to avoid leak on re-render
  const fileObjectUrls = useMemo(() => {
    const urls = files.map(f => f.type?.startsWith('image/') ? URL.createObjectURL(f) : null)
    return urls
  }, [files])

  // Revoke object URLs on cleanup
  useEffect(() => {
    return () => {
      fileObjectUrls.forEach(url => { if (url) URL.revokeObjectURL(url) })
    }
  }, [fileObjectUrls])

  const canSubmit = selectedOption.requiresFiles
    ? (files.length > 0 || (existingFiles?.length ?? 0) > 0)
    : true
  const isDisabled = !canSubmit || isCreating

  const handleSubmit = useCallback(() => {
    if (isDisabled) return
    // Pass files for all types (chat passes them for optional context)
    onWorkspaceCreated(patientLabel.trim() || '', selectedType, files)
  }, [isDisabled, files, selectedType, patientLabel, onWorkspaceCreated])

  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="w-full max-w-lg animate-fade-in">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-display font-bold text-foreground mb-1.5">
            New Workspace
          </h1>
          <p className="text-sm text-muted-foreground">
            {isOverlayHandoff
              ? 'Review attached files and start your workspace.'
              : selectedType === 'chat'
                ? 'Select a workspace type to begin.'
                : 'Select a workspace type, then upload chart data to begin.'}
          </p>
        </div>

        {/* Workspace type selector — hidden in overlay-handoff mode (locked to admission) */}
        {!isOverlayHandoff && (
          <div className="grid grid-cols-3 gap-2 mb-5">
            {WORKSPACE_TYPES.map(option => {
              const Icon = option.icon
              const isSelected = selectedType === option.type
              return (
                <button
                  key={option.type}
                  onClick={() => handleTypeChange(option.type)}
                  className={cn(
                    'flex flex-col items-start p-3 rounded-xl border-2 transition-all text-left',
                    isSelected
                      ? `${option.accentBorder} ${option.accentBg} shadow-lg ${option.accentShadow}`
                      : 'border-border bg-card hover:border-muted-foreground/30'
                  )}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className={cn('w-4 h-4', isSelected ? option.accent : 'text-muted-foreground')} />
                    <span className={cn(
                      'text-xs font-semibold',
                      isSelected ? option.accent : 'text-muted-foreground'
                    )}>
                      {option.label}
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
                    {option.description}
                  </p>
                </button>
              )
            })}
          </div>
        )}

        {/* Patient label input */}
        <div className="mb-4">
          <Input
            type="text"
            placeholder="Patient label (optional) -- e.g. 72M chest pain"
            value={patientLabel}
            onChange={e => setPatientLabel(e.target.value)}
            className="h-9 text-sm bg-card border-border"
          />
        </div>

        {/* Drop zone -- shown when acceptTypes is non-empty (all types now) */}
        {selectedOption.acceptTypes && (
          <Card
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={cn(
              'relative rounded-2xl border-2 border-dashed transition-all duration-200',
              dragOver
                ? 'border-primary bg-primary/5 scale-[1.01]'
                : 'border-border bg-card hover:border-muted-foreground/40 hover:bg-muted/30',
            )}
          >
            {/* Native label→input: browser opens file dialog, no JS needed */}
            <input
              id="intake-file-input"
              ref={fileInputRef}
              type="file"
              multiple
              accept={selectedOption.acceptTypes}
              onChange={handleFileSelect}
              className="sr-only"
            />

            {/* Server-side files from the overlay — collapsed to a single count
                row. The full list of UUID-named PNGs pushed the Start button
                off-screen and gave the user no actionable information. */}
            {existingFiles && existingFiles.length > 0 && (
              <div className="flex items-center gap-2 py-3 px-5 border-b border-border/40 text-xs text-muted-foreground">
                <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  {existingFiles.length}{' '}
                  {existingFiles.every((f) => f.content_category === 'image')
                    ? `screenshot${existingFiles.length === 1 ? '' : 's'}`
                    : `file${existingFiles.length === 1 ? '' : 's'}`}{' '}
                  uploaded
                </span>
              </div>
            )}

            {files.length === 0 ? (
              <label htmlFor="intake-file-input" className="block cursor-pointer text-center py-14 px-6">
                <Upload className="w-8 h-8 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground font-medium mb-1">
                  {selectedType === 'flow'
                    ? 'Drop text files here, or click to browse'
                    : selectedType === 'chat'
                      ? 'Drop files here for context (optional)'
                      : 'Drop chart files here, or click to browse'}
                </p>
                <p className="text-xs text-muted-foreground/60">
                  {selectedType === 'flow'
                    ? 'Unstructured text files for IPASS signout'
                    : selectedType === 'chat'
                      ? 'PDFs, images, or text files — Casey will use them as context'
                      : 'Encounter notes, med lists, lab results, images'}
                </p>
              </label>
            ) : (
              <label htmlFor="intake-file-input" className="block cursor-pointer py-5 px-5">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {files.length} file{files.length !== 1 ? 's' : ''} attached
                  </span>
                  <span className="text-[10px] text-muted-foreground/50">
                    Click or drop to add more
                  </span>
                </div>
                <div className="space-y-1.5">
                  {files.map((file, i) => (
                    <div
                      key={`${file.name}-${i}`}
                      className="flex items-center justify-between bg-muted/50 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {fileObjectUrls[i] ? (
                          <img
                            src={fileObjectUrls[i]!}
                            alt={file.name}
                            className="w-8 h-8 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <FileText className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0" />
                        )}
                        <span className="text-xs text-foreground/80 truncate">{file.name}</span>
                        <span className="text-[10px] text-muted-foreground/50 flex-shrink-0">
                          {(file.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeFile(i) }}
                        className="text-muted-foreground/50 hover:text-destructive text-xs ml-2 transition-colors p-0.5"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </label>
            )}
          </Card>
        )}

        {/* Rejected file error */}
        {rejectedFiles.length > 0 && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-destructive/5 border border-destructive/20 text-xs text-destructive">
            {rejectedFiles.map((msg, i) => <div key={i}>{msg}</div>)}
          </div>
        )}

        {/* Submit button */}
        <Button
          onClick={handleSubmit}
          disabled={isDisabled}
          size="lg"
          className={cn(
            'w-full mt-4 rounded-xl text-sm font-semibold transition-all duration-200',
            canSubmit && !isCreating
              ? `${selectedOption.accentBg} text-background hover:opacity-90 border ${selectedOption.accentBorder}`
              : ''
          )}
          variant={canSubmit && !isCreating ? 'default' : 'secondary'}
        >
          {isCreating
            ? 'Creating workspace...'
            : selectedType === 'chat' && files.length === 0
              ? 'Start Chat'
              : selectedType === 'chat' && files.length > 0
                ? `Start with ${files.length} file${files.length !== 1 ? 's' : ''}`
                : selectedType === 'flow'
                  ? 'Start Flow'
                  : files.length > 0
                    ? `Start with ${files.length} file${files.length !== 1 ? 's' : ''}`
                    : (existingFiles?.length ?? 0) > 0
                      ? `Start with ${existingFiles!.length} file${existingFiles!.length !== 1 ? 's' : ''}`
                      : 'Upload files to begin'}
        </Button>

        {/* Ambient Scribe -- beta access on the front page */}
        {encounterStatus === 'idle' && (
          <div className="mt-3">
            <button
              onClick={() => startEncounter('soap')}
              className={cn(
                'w-full flex items-center justify-center gap-2 rounded-xl px-4 py-2.5',
                'bg-muted/50 hover:bg-muted border border-border/50 hover:border-border',
                'text-sm text-muted-foreground hover:text-foreground transition-all'
              )}
            >
              <Mic className="h-4 w-4" />
              <span>Ambient Scribe</span>
              <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                Beta
              </span>
            </button>
          </div>
        )}

        <p className="text-center text-[10px] text-muted-foreground/40 mt-4">
          All content is AI-drafted and requires physician review
        </p>
      </div>
    </div>
  )
}


